import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { DeleteOutline } from '@mui/icons-material';

import AddButtonAutocomplete from '@/__core/hocs/AddButtonAutocomplete';
import RequestMaterialItemCreateModal from '@/modules/inventory/requestMaterial/components/RequestMaterialItemCreateModal';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { calculateTableWidth } from '@/utils/tableWidth';
import { useSidebarState } from '@/__core/hooks/useSidebarState';

import { ReceiptLine } from '../types';

const tableStyles = sharedFormStyles.orderItemsTableStyles;
const PAPER_HORIZONTAL_PADDING = 0;

interface ReceiptLinesTableProps {
  lines: ReceiptLine[];
  onChangeLine: (lineId: string, updates: Partial<ReceiptLine>) => void;
  onDeleteLines: (lineIds: string[]) => void;
  itemOptions?: Array<{ id: string; name: string; code: string; supplier_name?: string; unit_id?: string; unit_name?: string }>;
  uomOptions?: Array<{ id: string; name: string; code?: string }>;
  itemLoading?: boolean;
  allowAddItem?: boolean;
  onAddItem?: () => void;
  disabled?: boolean;
  companyId?: string;
  companyName?: string;
  onCreateItem?: (item: { id: string; name: string; code: string; unit_id?: string; unit_name?: string }) => void;
  canCreateItem?: boolean;
  searchItems?: (query: string) => Promise<Array<{ id: string; name: string; code: string; unit_id?: string; unit_name?: string }>>;
  searchLoading?: boolean;
}

const editableCellSx = {
  '& .MuiInputBase-root': {
    height: '30px',
    minHeight: '30px',
    maxHeight: '30px',
    backgroundColor: 'transparent',
    overflow: 'visible',
    border: 'none !important',
    borderBottom: '1px solid #d1d5db',
    borderRadius: 0,
    '&:hover': {
      borderBottom: '1px solid #9ca3af',
    },
    '&.Mui-focused': {
      borderBottom: '1px solid #3b82f6 !important',
      border: 'none !important',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none !important',
  },
  '& .MuiInputBase-input': {
    padding: '6px 0',
    fontSize: '0.875rem',
    color: '#374151 !important',
  },
};

export default function ReceiptLinesTable({
  lines,
  onChangeLine,
  onDeleteLines,
  itemOptions = [],
  uomOptions = [],
  itemLoading = false,
  allowAddItem = false,
  onAddItem,
  disabled = false,
  companyId = '',
  companyName,
  onCreateItem,
  canCreateItem = false,
  searchItems,
  searchLoading = false,
}: ReceiptLinesTableProps) {
  const [selectedLineIds, setSelectedLineIds] = React.useState<string[]>([]);
  const [manualItemOptions] = React.useState<Array<{ id: string; name: string; code: string; supplier_name?: string; unit_id?: string; unit_name?: string }>>([]);
  const [manualUomOptions, setManualUomOptions] = React.useState<Array<{ id: string; name: string; code?: string }>>([]);
  const [tableWidth, setTableWidth] = React.useState<number>(0);
  const [searchResults, setSearchResults] = React.useState<Array<{ id: string; name: string; code: string; supplier_name?: string; unit_id?: string; unit_name?: string }>>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const searchInputRef = React.useRef('');
  const sidebarOpen = useSidebarState();

  const ItemCreateModal = React.useMemo(
    () => (props: { open: boolean; onClose: () => void; onSave: (data: any) => Promise<any>; loading?: boolean }) => (
      <RequestMaterialItemCreateModal {...props} companyId={companyId} companyName={companyName} />
    ),
    [companyId, companyName],
  );
  const displayLines = lines;
  const effectiveItemOptions = React.useMemo(() => {
    const merged = isSearching ? searchResults : [...itemOptions, ...manualItemOptions, ...searchResults];
    const unique = new Map<string, { id: string; name: string; code: string; supplier_name?: string; unit_id?: string; unit_name?: string }>();
    merged.forEach((item) => {
      if (!unique.has(item.id)) {
        unique.set(item.id, item);
      }
    });
    return Array.from(unique.values());
  }, [itemOptions, manualItemOptions]);
  const effectiveUomOptions = React.useMemo(() => {
    const merged = [...uomOptions, ...manualUomOptions];
    const unique = new Map<string, { id: string; name: string; code?: string }>();
    merged.forEach((uom) => {
      if (!unique.has(uom.id)) {
        unique.set(uom.id, uom);
      }
    });
    return Array.from(unique.values());
  }, [uomOptions, manualUomOptions]);
  const groupedItemOptions = React.useMemo(
    () => [...effectiveItemOptions].sort((left, right) => {
      const leftSupplier = left.supplier_name?.trim() || 'Chua co nha cung cap';
      const rightSupplier = right.supplier_name?.trim() || 'Chua co nha cung cap';

      return leftSupplier.localeCompare(rightSupplier, 'vi')
        || (left.code || '').localeCompare(right.code || '', 'vi')
        || (left.name || '').localeCompare(right.name || '', 'vi');
    }),
    [effectiveItemOptions],
  );
  const allSelected = displayLines.length > 0 && displayLines.every((line) => selectedLineIds.includes(line.id));
  const selectedCount = selectedLineIds.length;

  React.useEffect(() => {
    const itemLineIds = new Set(displayLines.map((line) => line.id));
    setSelectedLineIds((current) => current.filter((id) => itemLineIds.has(id)));
  }, [displayLines]);

  React.useEffect(() => {
    const handleResize = () => {
      setTableWidth(calculateTableWidth({
        windowWidth: window.innerWidth,
        sidebarOpen,
      }));
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLineIds(displayLines.map((line) => line.id));
      return;
    }
    setSelectedLineIds([]);
  };

  const toggleSelectLine = (lineId: string, checked: boolean) => {
    setSelectedLineIds((current) => {
      if (checked) {
        if (current.includes(lineId)) {
          return current;
        }
        return [...current, lineId];
      }
      return current.filter((id) => id !== lineId);
    });
  };

  const handleDeleteSelected = () => {
    if (selectedLineIds.length === 0) {
      return;
    }
    onDeleteLines(selectedLineIds);
    setSelectedLineIds([]);
  };

  const totalAmount = lines.reduce((sum, line) => {
    const received = Number(line.received_qty || 0);
    const unitCost = Number(line.unit_cost || 0);
    return sum + (isNaN(received) || isNaN(unitCost) ? 0 : received * unitCost);
  }, 0);

  const totalReceivedQty = lines.reduce((sum, line) => sum + (Number(line.received_qty) || 0), 0);


  return (
    <Box sx={{ ...tableStyles.sectionWrapper, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      <TableContainer
        sx={{
          ...tableStyles.tableContainer(tableWidth, PAPER_HORIZONTAL_PADDING),
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        <Table size='small' sx={{ ...tableStyles.table, width: '100%' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell style={{ width: '48px', minWidth: '48px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>
                <Checkbox
                  size='small'
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  onChange={(event) => toggleSelectAll(event.target.checked)}
                  disabled={disabled}
                  sx={{ p: 0.5 }}
                />
              </TableCell>
              <TableCell style={{ width: '40px', minWidth: '40px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>#</TableCell>
              <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151' }}>Mã VT</TableCell>
              <TableCell style={{ width: '280px', minWidth: '280px' }} sx={{ fontWeight: 600, color: '#374151' }}>Tên vật tư</TableCell>
              <TableCell style={{ width: '100px', minWidth: '140px' }} sx={{ fontWeight: 600, color: '#374151' }}>ĐVT</TableCell>
              <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>SL nhận</TableCell>
              <TableCell style={{ width: '140px', minWidth: '140px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>Đơn giá</TableCell>
              <TableCell style={{ width: '150px', minWidth: '150px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>Thành tiền</TableCell>
              <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151' }}>Số lô</TableCell>
              <TableCell style={{ width: '220px', minWidth: '220px' }} sx={{ fontWeight: 600, color: '#374151' }}>Ghi chú</TableCell>
              <TableCell style={{ width: '48px', minWidth: '48px' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {displayLines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4, color: '#9ca3af', fontSize: '0.875rem' }}>
                  Chưa có dòng vật tư. Hãy chọn PO để tự sinh danh sách hoặc thêm bằng API backend.
                </TableCell>
              </TableRow>
            ) : (
              displayLines.map((line, index) => {
                const lineAmount = Number(line.received_qty || 0) * Number(line.unit_cost || 0);
                const selectedItem = effectiveItemOptions.find((item) => item.id === line.item_id) || null;
                const selectedUom = effectiveUomOptions.find((uom) => uom.id === line.uom_id) || null;
                return (
                  <TableRow key={line.id} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Checkbox
                        size='small'
                        checked={selectedLineIds.includes(line.id)}
                        onChange={(event) => toggleSelectLine(line.id, event.target.checked)}
                        disabled={disabled}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', color: '#6b7280', fontSize: '0.75rem', fontWeight: 500 }}>{index + 1}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem', color: '#374151', fontFamily: '"Roboto Mono", monospace' }}>
                        {selectedItem?.code || line.item_code || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <AddButtonAutocomplete
                        options={groupedItemOptions}
                        value={selectedItem || (line.item_id ? { id: line.item_id, name: line.item_name, code: line.item_code } : null)}
                        getOptionLabel={(option) => option?.name || ''}
                        renderOption={(props, option) => (
                          <Box component='li' {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827' }}>
                              {option.name}
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#6b7280' }}>
                              {option?.supplier_name?.trim() || 'Chua co nha cung cap'}
                            </Typography>
                          </Box>
                        )}
                        onChange={(_event, newValue) => {
                          const nextItem = newValue as { id: string; name: string; code: string; unit_id?: string; unit_name?: string; last_price?: string } | null;
                          const lastPrice = Number(nextItem?.last_price) || 0;
                          onChangeLine(line.id, {
                            item_id: nextItem?.id || '',
                            item_code: nextItem?.code || '',
                            item_name: nextItem?.name || '',
                            uom_id: nextItem?.unit_id || line.uom_id || '',
                            uom_name: nextItem?.unit_name || line.uom_name || '',
                            unit_cost: lastPrice ? String(lastPrice) : line.unit_cost,
                          });
                        }}
                        onInputChange={(_event, value, reason) => {
                          if (reason === 'input' && searchItems && value.trim()) {
                            searchInputRef.current = value;
                            setIsSearching(true);
                            searchItems(value.trim()).then((results) => {
                              if (searchInputRef.current === value) {
                                setSearchResults(results);
                              }
                            });
                          } else if (reason === 'clear' || !value.trim()) {
                            searchInputRef.current = '';
                            setIsSearching(false);
                            setSearchResults([]);
                          }
                        }}
                        onAddNewCustom={async (createdItem: { id: string; name: string; code: string; unit_id?: string; unit_name?: string }) => {
                          onChangeLine(line.id, {
                            item_id: createdItem.id,
                            item_code: createdItem.code,
                            item_name: createdItem.name,
                            uom_id: createdItem.unit_id || line.uom_id || '',
                            uom_name: createdItem.unit_name || line.uom_name || '',
                          });
                          onCreateItem?.(createdItem);
                          return createdItem;
                        }}
                        CustomDialog={ItemCreateModal}
                        placeholder='Chọn vật tư'
                        noOptionsText='Không tìm thấy vật tư'
                        loading={itemLoading || searchLoading}
                        disableClearable={Boolean(line.item_id)}
                        disabled={disabled}
                        canAddNew={!disabled && canCreateItem}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size='small'
                            placeholder='Chọn vật tư'
                            sx={{
                              '& .MuiAutocomplete-clearIndicator': { display: 'none' },
                              '& .MuiAutocomplete-popupIndicator': { display: 'none' },
                            }}
                          />
                        )}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell>
                      <AddButtonAutocomplete
                        options={effectiveUomOptions}
                        value={selectedUom || (line.uom_id ? { id: line.uom_id, name: line.uom_name } : null)}
                        onChange={(_event, newValue) => {
                          const nextUom = newValue as { id: string; name: string } | null;
                          onChangeLine(line.id, {
                            uom_id: nextUom?.id || '',
                            uom_name: nextUom?.name || '',
                          });
                        }}
                        onAddNew={async ({ name }) => {
                          const created = {
                            id: `manual-uom-${Date.now()}`,
                            name: name.trim(),
                            code: name.trim(),
                          };
                          setManualUomOptions((current) => [...current, created]);
                          return created;
                        }}
                        placeholder='Chọn ĐVT'
                        noOptionsText='Không tìm thấy ĐVT'
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        customInput={TextField}
                        size='small'
                        value={line.received_qty}
                        onValueChange={({ value }) => onChangeLine(line.id, { received_qty: value })}
                        decimalScale={2}
                        fixedDecimalScale
                        thousandSeparator='.'
                        decimalSeparator=','
                        allowNegative={false}
                        inputProps={{ style: { textAlign: 'right' } }}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        value={line.unit_cost}
                        onValueChange={({ value }) => onChangeLine(line.id, { unit_cost: value || '0' })}
                        decimalScale={0}
                        thousandSeparator='.'
                        decimalSeparator=','
                        allowNegative={false}
                        customInput={TextField}
                        size='small'
                        inputProps={{ style: { textAlign: 'right' } }}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <NumericFormat
                        value={lineAmount}
                        displayType='text'
                        thousandSeparator='.'
                        decimalSeparator=','
                        decimalScale={0}
                        renderText={(value) => (
                          <Typography sx={{ fontSize: '0.875rem', color: '#1e293b', fontWeight: 500, fontFamily: '"Roboto Mono", monospace' }}>
                            {value}
                          </Typography>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size='small'
                        value={line.lot_no}
                        onChange={(event) => onChangeLine(line.id, { lot_no: event.target.value })}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size='small'
                        value={line.note}
                        onChange={(event) => onChangeLine(line.id, { note: event.target.value })}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <IconButton
                        size='small'
                        onClick={() => onDeleteLines([line.id])}
                        disabled={disabled}
                        sx={{ color: '#ef4444' }}
                      >
                        <DeleteOutline fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          {displayLines.length > 0 && (
            <tfoot>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                <TableCell colSpan={5} sx={{ py: 0.5, borderTop: '2px solid #e5e7eb' }} />
                <TableCell sx={{ textAlign: 'right', fontWeight: 700, fontSize: '0.875rem', color: '#111827', py: 0.5, borderTop: '2px solid #e5e7eb' }}>
                  {totalReceivedQty.toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell sx={{ py: 0.5, borderTop: '2px solid #e5e7eb' }} />
                <TableCell sx={{ textAlign: 'right', fontWeight: 700, fontSize: '0.875rem', color: '#111827', py: 0.5, borderTop: '2px solid #e5e7eb' }}>
                  {totalAmount.toLocaleString('vi-VN', { maximumFractionDigits: 0 })}
                </TableCell>
                <TableCell sx={{ py: 0.5, borderTop: '2px solid #e5e7eb' }} />
                <TableCell sx={{ py: 0.5, borderTop: '2px solid #e5e7eb' }} />
                <TableCell sx={{ py: 0.5, borderTop: '2px solid #e5e7eb' }} />
              </TableRow>
            </tfoot>
          )}
        </Table>
      </TableContainer>

      {(displayLines.length > 0 || allowAddItem) && (
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 1, gap: 2 }}>
          {allowAddItem && (
            <Button
              variant='contained'
              size='small'
              onClick={onAddItem}
              disabled={disabled}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Thêm item
            </Button>
          )}
          {selectedCount > 0 && (
            <Button
              variant='outlined'
              color='error'
              size='small'
              onClick={handleDeleteSelected}
              disabled={disabled}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', py: 0.25 }}
            >
              Xóa ({selectedCount})
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
